import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import {
  Scissors, Copy, Check, Sparkles, ShoppingBag, Clock, AlertTriangle, ArrowLeft, ChevronDown, Trash2,
} from "lucide-react";

// ---- Placeholder shop options (no real brands, clipart-style SVG) ----
// Three deterministic tiers per category. Real affiliate products will replace these.
const PLACEHOLDER_TIERS = [
  { tier: "Everyday", price: 14.99, hue: 200 },
  { tier: "Studio",   price: 22.99, hue: 30  },
  { tier: "Pro",      price: 32.99, hue: 280 },
];

const CATEGORY_SHAPE = {
  "Base / Prep":       "spray",
  "Blow Dry / Style":  "dryer",
  "Volumizing Powder": "powder",
  "Hold & Finish":     "jar",
  "Lock / Hairspray":  "can",
};

function ProductClipart({ shape = "jar", hue = 30 }) {
  const fill = `hsl(${hue}, 28%, 32%)`;
  const accent = `hsl(${hue}, 45%, 55%)`;
  // Different simple silhouettes per product family
  if (shape === "spray") {
    return (
      <svg viewBox="0 0 80 110" className="w-full h-full">
        <rect x="30" y="4" width="20" height="10" rx="2" fill="#1c1f26" />
        <rect x="46" y="8" width="22" height="6" rx="2" fill="#1c1f26" />
        <rect x="22" y="18" width="36" height="86" rx="6" fill={fill} />
        <rect x="28" y="48" width="24" height="32" fill="#f7f7f5" opacity="0.95" />
        <rect x="28" y="48" width="24" height="6" fill={accent} />
      </svg>
    );
  }
  if (shape === "dryer") {
    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <rect x="20" y="22" width="48" height="32" rx="6" fill={fill} />
        <circle cx="22" cy="38" r="10" fill={fill} />
        <rect x="62" y="30" width="22" height="16" rx="3" fill={accent} />
        <rect x="36" y="54" width="14" height="32" rx="3" fill={fill} />
        <rect x="32" y="84" width="22" height="6" rx="2" fill="#1c1f26" />
      </svg>
    );
  }
  if (shape === "powder") {
    return (
      <svg viewBox="0 0 80 110" className="w-full h-full">
        <rect x="20" y="14" width="40" height="10" rx="3" fill="#1c1f26" />
        <rect x="16" y="24" width="48" height="78" rx="6" fill={fill} />
        <circle cx="40" cy="40" r="4" fill="#f7f7f5" />
        <circle cx="30" cy="50" r="3" fill="#f7f7f5" opacity="0.8" />
        <circle cx="50" cy="52" r="3" fill="#f7f7f5" opacity="0.8" />
        <rect x="24" y="68" width="32" height="20" fill={accent} opacity="0.85" />
      </svg>
    );
  }
  if (shape === "can") {
    return (
      <svg viewBox="0 0 80 110" className="w-full h-full">
        <rect x="32" y="4" width="16" height="8" rx="2" fill="#1c1f26" />
        <rect x="36" y="12" width="8" height="8" fill="#1c1f26" />
        <rect x="18" y="20" width="44" height="84" rx="4" fill={fill} />
        <rect x="22" y="48" width="36" height="36" fill="#f7f7f5" opacity="0.95" />
        <rect x="22" y="48" width="36" height="6" fill={accent} />
      </svg>
    );
  }
  // jar (default)
  return (
    <svg viewBox="0 0 90 90" className="w-full h-full">
      <rect x="16" y="10" width="58" height="14" rx="3" fill="#1c1f26" />
      <rect x="10" y="22" width="70" height="60" rx="8" fill={fill} />
      <rect x="20" y="42" width="50" height="22" fill="#f7f7f5" opacity="0.95" />
      <rect x="20" y="42" width="50" height="5" fill={accent} />
    </svg>
  );
}

function ShopOptions({ category }) {
  const shape = CATEGORY_SHAPE[category] || "jar";
  return (
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="shop-options">
      {PLACEHOLDER_TIERS.map((t) => (
        <div
          key={t.tier}
          className="flex items-center gap-3 p-3 rounded-lg border border-ink-700 bg-ink-900/40 hover:border-copper/50 transition-all"
          data-testid={`shop-option-${t.tier.toLowerCase()}`}
        >
          <div className="w-12 h-12 flex-shrink-0 rounded-md bg-ink-950/60 p-1 flex items-center justify-center">
            <ProductClipart shape={shape} hue={t.hue} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400 uppercase tracking-wider">{t.tier} pick</p>
            <p className="text-sm text-platinum font-semibold">${t.price.toFixed(2)}</p>
          </div>
          <button
            type="button"
            className="cm-pill cm-pill-muted text-[10px] cursor-not-allowed opacity-70"
            disabled
            title="Coming soon"
          >
            Buy
          </button>
        </div>
      ))}
      <p className="sm:col-span-3 text-[10px] text-gray-600 uppercase tracking-widest">
        Placeholder — affiliate links coming soon
      </p>
    </div>
  );
}

export default function ScanResult() {
  const { id } = useParams();
  const location = useLocation();
  const [scan, setScan] = useState(location.state?.scan || null);
  const [loading, setLoading] = useState(!scan);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [openShop, setOpenShop] = useState({});

  useEffect(() => {
    if (scan) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/scans/${id}`);
        if (!cancelled) setScan(data);
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Could not load scan.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, scan]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-10">
        <p className="cm-label">Loading…</p>
      </div>
    );
  }
  if (error || !scan) {
    return (
      <div className="max-w-5xl mx-auto p-10">
        <div className="cm-card p-8 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400" /> {error || "Scan not found."}
        </div>
      </div>
    );
  }

  const ai = scan.ai_result || {};
  const aiError = ai.error;
  const products = ai.products || [];
  const steps = ai.styling_steps || [];
  const correction = scan.correction;

  // Prefer admin-corrected values if available
  const styleName = correction?.style_name || ai.style_name || "Unnamed style";
  const barberScript = correction?.barber_script || ai.barber_script || "";
  const finalProducts = correction?.products || products;

  const copyScript = async () => {
    if (!barberScript) return;
    try {
      await navigator.clipboard.writeText(barberScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Clipboard write failed:", err);
    }
  };

  if (aiError) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 animate-fade-in">
        <Link to="/scan" className="cm-label text-copper inline-flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="cm-card p-10 mt-6">
          <AlertTriangle className="w-8 h-8 text-copper" />
          <h1 className="cm-heading text-3xl mt-4">We couldn't read that one.</h1>
          <p className="mt-3 text-gray-400">{ai.message || "Try a clearer photo of the head from the front or side."}</p>
          <Link to="/scan" className="cm-btn-primary inline-flex mt-6">Try another photo</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-fade-in" data-testid="scan-result-page">
      <Link to="/dashboard" className="cm-label text-gray-400 hover:text-copper inline-flex items-center gap-2 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </Link>
      <p className="cm-label">SCAN RESULT</p>
      <h1 className="cm-heading text-4xl sm:text-5xl lg:text-6xl mt-3" data-testid="scan-result-style-name">
        {styleName}
      </h1>

      {/* Bento Grid */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <div className="cm-card overflow-hidden relative">
            {scan.storage_path ? (
              <>
                <img
                  src={fileUrl(scan.storage_path)}
                  alt={styleName}
                  className="w-full max-h-[640px] object-cover"
                  data-testid="scan-result-image"
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm("Delete the saved photo from your account? Your text results will be kept.")) return;
                    try {
                      await api.delete(`/scans/${scan.id}/photo`);
                      setScan({ ...scan, storage_path: undefined, photo_saved_by_user: false });
                    } catch {/* ignore */}
                  }}
                  className="absolute top-3 right-3 inline-flex items-center gap-2 px-3 py-2 rounded-md bg-ink-950/80 backdrop-blur border border-ink-700 text-platinum hover:border-red-400 hover:text-red-400 transition-colors text-xs"
                  data-testid="scan-result-delete-photo"
                >
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" /> Delete photo
                </button>
              </>
            ) : (
              <div
                className="w-full aspect-[4/3] flex flex-col items-center justify-center bg-gradient-to-br from-ink-900 to-ink-950 p-8 text-center"
                data-testid="scan-result-photo-purged"
              >
                <Scissors className="w-10 h-10 text-copper mb-4" aria-hidden="true" />
                <p className="cm-label">PHOTO PURGED</p>
                <p className="mt-3 text-sm text-gray-400 max-w-sm leading-relaxed">
                  {scan.photo_deleted_at
                    ? "You deleted this photo from your account. Your text recommendations are kept."
                    : "As promised in our Privacy Policy, your photo and any facial-geometry data were deleted immediately after analysis. Only the text recommendations below are kept."}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="cm-card p-5">
              <p className="cm-label">RE-CUT IN</p>
              <p className="cm-heading text-2xl mt-2">{ai.maintenance_weeks || 4} wks</p>
            </div>
            <div className="cm-card p-5">
              <p className="cm-label">BEST FOR</p>
              <p className="text-sm text-gray-300 mt-2 leading-snug">{ai.hair_type_match || "Most hair types"}</p>
            </div>
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="cm-card p-7 border-copper/40">
            <div className="flex items-center gap-2 mb-3">
              <Scissors className="w-5 h-5 text-copper" />
              <p className="cm-label">SAY THIS TO YOUR BARBER</p>
            </div>
            <p
              className="text-gray-200 leading-relaxed whitespace-pre-line"
              data-testid="scan-result-barber-script"
            >
              {barberScript || "—"}
            </p>
            <button
              onClick={copyScript}
              className="cm-btn-secondary mt-6 w-full inline-flex items-center justify-center gap-2"
              data-testid="copy-script-button"
            >
              {copied ? (<><Check className="w-4 h-4" /> Copied</>) : (<><Copy className="w-4 h-4" /> Copy script</>)}
            </button>
          </div>

          {/* Always-on disclaimer / pro tip */}
          <div className="cm-card p-5 mt-4" data-testid="barber-disclaimer">
            <p className="cm-label">PRO TIP</p>
            <p className="text-xs text-gray-400 mt-2 leading-relaxed italic">
              "It's always best to communicate with your barber or stylist — and show them a few
              reference pictures — to actually land your desired look."
            </p>
          </div>

          {correction && (
            <div className="cm-card p-5 mt-4 border-copper/30">
              <p className="cm-label">EDITED BY A REAL STYLIST</p>
              <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                A HairMaxxing Pro expert refined this result.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Products — ordered routine */}
      <div className="mt-14">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingBag className="w-5 h-5 text-copper" />
          <p className="cm-label">THE ROUTINE — IN ORDER</p>
        </div>
        <p className="text-sm text-gray-500 mb-6 max-w-2xl">
          Apply in this order. Skip any step marked "optional" if your hair doesn't need it.
        </p>
        <div className="space-y-4">
          {(() => {
            // Sort by order if available; fall back to source order
            const sorted = [...finalProducts].sort((a, b) => {
              const ao = typeof a.order === "number" ? a.order : 999;
              const bo = typeof b.order === "number" ? b.order : 999;
              return ao - bo;
            });
            return sorted.map((p, i) => {
              const stepNo = i + 1;
              const category = p.category || "Product";
              const hold = p.hold_level && p.hold_level !== "None" ? p.hold_level : null;
              const shine = p.shine_level || null;
              return (
                <div
                  key={`${p.name || "product"}-${i}`}
                  className="cm-card p-6 flex gap-5 hover:border-copper/40 transition-all"
                  data-testid={`product-card-${i}`}
                >
                  <div className="flex-shrink-0">
                    <div className="cm-heading text-copper text-3xl w-12">
                      {String(stepNo).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="cm-pill cm-pill-platinum">{category}</span>
                      {p.optional && <span className="cm-pill cm-pill-muted">Optional</span>}
                      {hold && <span className="cm-pill cm-pill-muted">{hold} Hold</span>}
                      {shine && <span className="cm-pill cm-pill-muted">{shine} Shine</span>}
                    </div>
                    <h3 className="cm-heading text-xl">{p.name || "Product"}</h3>
                    {p.why && (
                      <p className="text-sm text-gray-400 mt-3 leading-relaxed">{p.why}</p>
                    )}
                    {p.how_to_use && (
                      <p className="text-xs text-gray-500 mt-3 leading-relaxed border-t border-ink-700 pt-3">
                        <span className="cm-label text-[10px] block mb-1">How to use</span>
                        {p.how_to_use}
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={() => setOpenShop((s) => ({ ...s, [i]: !s[i] }))}
                      className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-copper hover:text-copper/80 transition-colors"
                      data-testid={`shop-toggle-${i}`}
                    >
                      <ShoppingBag className="w-3.5 h-3.5" />
                      {openShop[i] ? "Hide options" : "Shop this product"}
                      <ChevronDown
                        className={`w-3.5 h-3.5 transition-transform ${openShop[i] ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openShop[i] && <ShopOptions category={category} />}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Styling steps */}
      {steps.length > 0 && (
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-5 h-5 text-copper" />
            <p className="cm-label">STYLE IT YOURSELF — STEP BY STEP</p>
          </div>
          <ol className="space-y-3">
            {steps.map((s, i) => (
              <li key={`step-${i}-${(s || "").slice(0, 20)}`} className="cm-card p-5 flex gap-4">
                <span className="cm-heading text-copper text-2xl w-10">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-gray-300 leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="mt-14 flex flex-wrap gap-4">
        <Link to="/scan" className="cm-btn-primary inline-flex" data-testid="scan-again-button">
          Scan another
        </Link>
        <Link to="/history" className="cm-btn-secondary inline-flex">
          <Clock className="w-4 h-4 mr-2" /> Scan history
        </Link>
      </div>
    </div>
  );
}
